import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/common/Navbar';
import {
  getChats,
  getAvailableUsers,
  getMessages,
  sendMessage,
  createChat,
  editMessage,
  deleteMessage,
  searchMessages,
  createGroupChat,
  uploadAttachment,
  getUnreadCount,
  markChatRead,
  getMessageAttachment
} from '../services/api';
import { formatDateTime, relativeTime, getInitials, formatChatListTime, formatTimeShort, formatDaySeparator } from '../utils/formatters';
import '../components/common/ui.css';
import './MessagesPage.css';

const POLL_INTERVAL = 5000;
const TOAST_DURATION = 6000;
const GROUP_GAP_MS = 5 * 60 * 1000; // сообщения одного автора ближе 5 минут схлопываются в одну группу
const SENDER_COLORS = ['#e64980', '#fa5252', '#fd7e14', '#f59f00', '#40c057', '#12b886', '#228be6', '#7950f2'];
const senderColor = (id) => SENDER_COLORS[Math.abs(Number(id) || 0) % SENDER_COLORS.length];
const dayKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

// Заглушка аватара группового чата — вместо эмодзи 👥
const GROUP_AVATAR_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="9" cy="6" r="4" stroke="currentColor" strokeWidth="1.5" />
    <path d="M15 9C16.6569 9 18 7.65685 18 6C18 4.34315 16.6569 3 15 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <ellipse cx="9" cy="17" rx="7" ry="4" stroke="currentColor" strokeWidth="1.5" />
    <path d="M18 14C19.7542 14.3847 21 15.3589 21 16.5C21 17.5293 19.9863 18.4229 18.5 18.8704" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const MessagesPage = () => {
  const { user, organization, isAuthenticated, logout } = useAuth();
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [editingMessage, setEditingMessage] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [chatError, setChatError] = useState('');
  const [modalError, setModalError] = useState('');

  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);
  const fileInputRef = useRef(null);
  const composerInputRef = useRef(null);
  // Хранят актуальные значения для колбэка setInterval, который создаётся
  // один раз при монтировании и иначе видел бы устаревшие chat/messages.
  const currentChatRef = useRef(null);
  const previousUnreadRef = useRef(null);
  const toastTimersRef = useRef({});

  useEffect(() => {
    currentChatRef.current = currentChat;
  }, [currentChat]);

  useEffect(() => {
    if (isAuthenticated) {
      loadChats();
      startPolling();
      updateTitleWithUnread(unreadCount);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      Object.values(toastTimersRef.current).forEach(clearTimeout);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (currentChat) {
      loadMessages(currentChat.id);
      markChatAsRead(currentChat.id);
      setChatError('');
    }
    // Зависимость только от id: currentChat также обновляется при каждом
    // опросе (свежий online-статус/последнее сообщение), это не должно
    // заново дёргать загрузку сообщений и отметку "прочитано".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Автоматический рост поля ввода вместо фиксированных 3 строк.
  useEffect(() => {
    const el = composerInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [messageInput]);

  useEffect(() => {
    updateTitleWithUnread(unreadCount);
  }, [unreadCount]);

  // Инлайн-поиск в сайдбаре вместо отдельной модалки: ищем по всем своим
  // чатам с небольшой задержкой, чтобы не дёргать API на каждый символ.
  useEffect(() => {
    const query = searchQuery.trim();
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const data = await searchMessages(query);
        setSearchResults(data.results || []);
      } catch (error) {
        console.error('Search error:', error);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const startPolling = () => {
    pollingRef.current = setInterval(() => {
      loadChats(true);
      if (currentChatRef.current) {
        loadMessages(currentChatRef.current.id, true);
      }
      updateUnreadCount();
    }, POLL_INTERVAL);
  };

  const updateUnreadCount = async () => {
    try {
      const data = await getUnreadCount();
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error('Error getting unread count:', error);
    }
  };

  const loadUsers = async () => {
  try {
    const data = await getAvailableUsers();
    setUsers(data.users);
  } catch (error) {
    console.error('Error loading users:', error);
  }
};

  const updateTitleWithUnread = (count) => {
    const orgName = organization?.name || '';
    if (count > 0) {
      document.title = `(${count}) 💬 Чат - ${orgName}`;
    } else {
      document.title = `💬 Чат - ${orgName}`;
    }
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,U3RlYWx0aCBzb3VuZA==');
      audio.volume = 0.3;
      audio.play().catch(e => console.log('Audio play failed:', e));
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  // Сравнивает unread_count каждого чата с прошлым опросом и показывает
  // всплывающее уведомление для новых сообщений в чатах, которые сейчас не открыты.
  const detectNewMessages = (newChats) => {
    const prevMap = previousUnreadRef.current;
    if (prevMap) {
      newChats.forEach(chat => {
        const prevCount = prevMap[chat.id] ?? 0;
        const isOpenChat = currentChatRef.current?.id === chat.id;
        if (chat.unread_count > prevCount && !isOpenChat) {
          showToast(chat);
          playNotificationSound();
        }
      });
    }
    const nextMap = {};
    newChats.forEach(c => { nextMap[c.id] = c.unread_count; });
    previousUnreadRef.current = nextMap;
  };

  const loadChats = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await getChats();
      detectNewMessages(data.chats);
      setChats(data.chats);
      setUnreadCount(data.unread_total);
      // Держим открытый чат синхронизированным (online-статус, последнее
      // сообщение), не трогая выбор, если он больше не в списке.
      setCurrentChat(prev => {
        if (!prev) return prev;
        return data.chats.find(c => c.id === prev.id) || prev;
      });
      return data.chats;
    } catch (error) {
      console.error('Error loading chats:', error);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadMessages = async (chatId, silent = false) => {
    try {
      const data = await getMessages(chatId);
      const newMessages = data.messages;
      setMessages(prevMessages => {
        // Пришло новое сообщение от собеседника, пока чат открыт — звук.
        if (silent && prevMessages.length > 0) {
          const lastMessage = newMessages[newMessages.length - 1];
          const previousLastMessage = prevMessages[prevMessages.length - 1];
          if (lastMessage && previousLastMessage &&
              lastMessage.id !== previousLastMessage.id &&
              lastMessage.user_id !== user?.id) {
            playNotificationSound();
          }
        }
        return newMessages;
      });
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const showToast = (chat) => {
    const id = `${chat.id}-${Date.now()}`;
    setToasts(prev => [...prev, {
      id,
      chatId: chat.id,
      name: chat.name,
      isGroup: chat.is_group,
      avatar: chat.is_group ? chat.avatar : chat.other_user_avatar,
      text: (chat.last_sender ? `${chat.last_sender}: ` : '') + (chat.last_message || '')
    }]);
    toastTimersRef.current[id] = setTimeout(() => dismissToast(id), TOAST_DURATION);
  };

  const dismissToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    clearTimeout(toastTimersRef.current[id]);
    delete toastTimersRef.current[id];
  };

  const openToastChat = (toast) => {
    const chat = chats.find(c => c.id === toast.chatId);
    if (chat) setCurrentChat(chat);
    dismissToast(toast.id);
  };

  const markChatAsRead = async (chatId) => {
    try {
      await markChatRead(chatId);
      loadChats(true);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() && attachments.length === 0) return;
    setChatError('');
    try {
      if (attachments.length > 0) {
        // Отправка с вложениями
        for (const attachment of attachments) {
          await uploadAttachment(currentChat.id, messageInput, attachment.data, attachment.type, attachment.name);
        }
        setAttachments([]);
      } else {
        await sendMessage(currentChat.id, messageInput, replyingTo?.id);
      }
      
      setMessageInput('');
      setReplyingTo(null);
      await loadMessages(currentChat.id);
      await loadChats();
    } catch (error) {
      console.error('Error sending message:', error);
      setChatError('Ошибка отправки сообщения');
    }
  };

  const handleEditMessage = async (messageId, newText) => {
    setChatError('');
    try {
      await editMessage(messageId, newText);
      await loadMessages(currentChat.id);
      setEditingMessage(null);
    } catch (error) {
      console.error('Error editing message:', error);
      setChatError('Ошибка редактирования сообщения');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (window.confirm('Удалить сообщение?')) {
      setChatError('');
      try {
        await deleteMessage(messageId);
        await loadMessages(currentChat.id);
      } catch (error) {
        console.error('Error deleting message:', error);
        setChatError('Ошибка удаления сообщения');
      }
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setAttachments(prev => [...prev, {
          data: event.target.result,
          type: file.type,
          name: file.name,
          size: file.size
        }]);
      };
      reader.readAsDataURL(file);
    });
    fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleOpenAttachment = async (messageId) => {
    setChatError('');
    try {
      const att = await getMessageAttachment(messageId);
      if (!att?.data) return;
      if (att.type && att.type.startsWith('image/')) {
        window.open(att.data, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = att.data;
        link.download = att.name || 'file';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error loading attachment:', error);
      setChatError('Ошибка загрузки вложения');
    }
  };

  const openSearchResult = (result) => {
    const chat = chats.find(c => c.id === result.chat_id);
    if (chat) setCurrentChat(chat);
    setSearchQuery('');
  };

  const handleCreateGroup = async () => {
    setModalError('');
    if (!groupName.trim() || selectedUsers.length === 0) {
      setModalError('Введите название группы и выберите участников');
      return;
    }
    try {
      const data = await createGroupChat(groupName, selectedUsers, groupDescription);
      setShowGroupModal(false);
      setGroupName('');
      setGroupDescription('');
      setSelectedUsers([]);
      const updatedChats = await loadChats();
      const newChat = updatedChats.find(c => c.id === data.chat_id);
      if (newChat) setCurrentChat(newChat);
    } catch (error) {
      console.error('Error creating group:', error);
      setModalError('Ошибка создания группы');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleNewChat = async (otherUserId) => {
    setModalError('');
    try {
      const data = await createChat(otherUserId);
      setShowNewChatModal(false);
      const updatedChats = await loadChats();
      const newChat = updatedChats.find(c => c.id === data.chat_id);
      if (newChat) setCurrentChat(newChat);
    } catch (error) {
      console.error('Error creating chat:', error);
      setModalError('Ошибка создания чата');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderAvatar = (avatarUrl, fallback) => (
    avatarUrl ? <img src={avatarUrl} alt="" className="avatar-img" /> : fallback
  );

  // Разбивка ленты на разделители дат и группы подряд идущих сообщений
  // одного автора — как в Телеграме: аватар/имя/хвостик пузыря только
  // у последнего сообщения в группе, а не у каждого.
  const messageGroups = useMemo(() => {
    const items = [];
    messages.forEach((msg, idx) => {
      const prev = messages[idx - 1];
      const sameDay = prev && dayKey(prev.created_at) === dayKey(msg.created_at);
      if (!sameDay) {
        items.push({ type: 'separator', key: `sep-${msg.id}`, date: msg.created_at });
      }
      const groupedWithPrev = sameDay
        && prev.user_id === msg.user_id
        && (new Date(msg.created_at) - new Date(prev.created_at)) < GROUP_GAP_MS;
      items.push({ type: 'message', key: msg.id, message: msg, isFirstInGroup: !groupedWithPrev });
    });
    for (let i = 0; i < items.length; i++) {
      if (items[i].type !== 'message') continue;
      const next = items[i + 1];
      items[i].isLastInGroup = !(next && next.type === 'message' && !next.isFirstInGroup);
    }
    return items;
  }, [messages]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!isAuthenticated) return null;

  return (
    <div className="messages-container">
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className="toast-item" onClick={() => openToastChat(toast)}>
            <div className="toast-avatar">
              {toast.avatar
                ? <img src={toast.avatar} alt="" className="avatar-img" />
                : (toast.isGroup ? GROUP_AVATAR_ICON : getInitials(toast.name))}
            </div>
            <div className="toast-body">
              <div className="toast-title">{toast.name}</div>
              <div className="toast-text">{toast.text}</div>
            </div>
            <button
              className="toast-close"
              onClick={(e) => { e.stopPropagation(); dismissToast(toast.id); }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M19.0068 5L5.00684 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M19 19L5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      <Navbar user={user} isAdmin={user?.role === 'admin'} onLogout={logout} />
      
      <div className="messages-wrapper">
        {/* Список чатов */}
        <div className="chats-sidebar">
          <div className="chats-header">
            <h2>
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="heading-icon">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 13.5997 2.37562 15.1116 3.04346 16.4525C3.22094 16.8088 3.28001 17.2161 3.17712 17.6006L2.58151 19.8267C2.32295 20.793 3.20701 21.677 4.17335 21.4185L6.39939 20.8229C6.78393 20.72 7.19121 20.7791 7.54753 20.9565C8.88837 21.6244 10.4003 22 12 22Z" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 12H8.009M11.991 12H12M15.991 12H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Сообщения
            </h2>
            <div className="chat-actions-header">
              <button
                className="icon-btn"
                onClick={() => { setModalError(''); loadUsers(); setShowNewChatModal(true); }}
                title="Новый личный чат"
                type="button"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M4 12L20 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M12.0204 4L12.0205 20.0003" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
              <button
                className="icon-btn"
                onClick={() => { setModalError(''); loadUsers(); setShowGroupModal(true); }}
                title="Новая группа"
                type="button"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="9" cy="6" r="4" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M15 9C16.6569 9 18 7.65685 18 6C18 4.34315 16.6569 3 15 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <ellipse cx="9" cy="17" rx="7" ry="4" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M18 14C19.7542 14.3847 21 15.3589 21 16.5C21 17.5293 19.9863 18.4229 18.5 18.8704" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          <div className="chats-search">
            <span className="chats-search-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="11.5" cy="11.5" r="9.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M18.5 18.5L22 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </span>
            <input
              type="text"
              className="chats-search-input"
              placeholder="Поиск по сообщениям"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="chats-search-clear"
                onClick={() => setSearchQuery('')}
                title="Очистить"
                type="button"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M19.0068 5L5.00684 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M19 19L5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>

          {!searchQuery && unreadCount > 0 && (
            <div className="unread-badge-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M22 10C22.0185 10.7271 22 11.0542 22 12C22 15.7712 22 17.6569 20.8284 18.8284C19.6569 20 17.7712 20 14 20H10C6.22876 20 4.34315 20 3.17157 18.8284C2 17.6569 2 15.7712 2 12C2 8.22876 2 6.34315 3.17157 5.17157C4.34315 4 6.22876 4 10 4H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M6 8L8.1589 9.79908C9.99553 11.3296 10.9139 12.0949 12 12.0949C13.0861 12.0949 14.0045 11.3296 15.8411 9.79908" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="19" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" />
              </svg>
              {' '}{unreadCount} непрочитанных
            </div>
          )}

          <div className="chats-list">
            {searchQuery.trim().length >= 2 ? (
              searchResults.length === 0 ? (
                <div className="no-chats">
                  <div className="no-chats-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <circle cx="11.5" cy="11.5" r="9.5" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M18.5 18.5L22 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div className="no-chats-text">Ничего не найдено</div>
                </div>
              ) : (
                searchResults.map(result => (
                  <div key={result.id} className="search-result-row" onClick={() => openSearchResult(result)}>
                    <div className="result-sender">{result.user_name}</div>
                    <div className="result-message">{result.message}</div>
                    <div className="result-time">{formatDateTime(result.created_at)}</div>
                  </div>
                ))
              )
            ) : chats.length === 0 ? (
              <div className="no-chats">
                <div className="no-chats-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 13.5997 2.37562 15.1116 3.04346 16.4525C3.22094 16.8088 3.28001 17.2161 3.17712 17.6006L2.58151 19.8267C2.32295 20.793 3.20701 21.677 4.17335 21.4185L6.39939 20.8229C6.78393 20.72 7.19121 20.7791 7.54753 20.9565C8.88837 21.6244 10.4003 22 12 22Z" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M8 12H8.009M11.991 12H12M15.991 12H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="no-chats-text">Нет чатов</div>
                <div className="no-chats-hint">Начните новый диалог</div>
              </div>
            ) : (
              chats.map(chat => (
                <div
                  key={chat.id}
                  className={`chat-item ${currentChat?.id === chat.id ? 'active' : ''}`}
                  onClick={() => setCurrentChat(chat)}
                >
                  <div className="chat-avatar">
                    {chat.is_group
                      ? renderAvatar(chat.avatar, GROUP_AVATAR_ICON)
                      : renderAvatar(chat.other_user_avatar, getInitials(chat.name))}
                    {!chat.is_group && chat.other_online && (
                      <span className="online-dot" title="В сети" />
                    )}
                  </div>
                  <div className="chat-info">
                    <div className="chat-row-top">
                      <div className="chat-name">{chat.name}</div>
                      {chat.last_message_time && (
                        <div className="chat-time">{formatChatListTime(chat.last_message_time)}</div>
                      )}
                    </div>
                    <div className="chat-row-bottom">
                      <div className="chat-last-message">
                        {chat.last_sender && `${chat.last_sender}: `}{chat.last_message || 'Нет сообщений'}
                      </div>
                      {chat.unread_count > 0 && (
                        <div className="chat-unread-badge">{chat.unread_count > 99 ? '99+' : chat.unread_count}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Область сообщений */}
        <div className="messages-area">
          {currentChat ? (
            <>
              <div className="messages-header">
                <div className="chat-avatar-large">
                  {currentChat.is_group
                    ? renderAvatar(currentChat.avatar, GROUP_AVATAR_ICON)
                    : renderAvatar(currentChat.other_user_avatar, getInitials(currentChat.name))}
                  {!currentChat.is_group && currentChat.other_online && (
                    <span className="online-dot large" title="В сети" />
                  )}
                </div>
                <div className="chat-info-large">
                  <div className="chat-name-large">{currentChat.name}</div>
                  <div className="chat-status">
                    {currentChat.is_group ? (
                      <>Групповой чат{currentChat.description && ` • ${currentChat.description}`}</>
                    ) : currentChat.other_online ? (
                      <span className="status-online">● В сети</span>
                    ) : (
                      <span>
                        {currentChat.other_last_seen
                          ? `Был(а) в сети ${relativeTime(currentChat.other_last_seen)}`
                          : 'Не в сети'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {chatError && (
                <div className="ui-inline-error messages-inline-error">{chatError}</div>
              )}

              {replyingTo && (
                <div className="reply-preview">
                  <div className="reply-content">
                    <span>Ответ на: {replyingTo.message.substring(0, 50)}...</span>
                    <button onClick={() => setReplyingTo(null)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M19.0068 5L5.00684 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M19 19L5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              
              <div className="messages-list">
                {messageGroups.map(item => {
                  if (item.type === 'separator') {
                    return (
                      <div key={item.key} className="date-separator">
                        <span>{formatDaySeparator(item.date)}</span>
                      </div>
                    );
                  }
                  const msg = item.message;
                  const isOwn = msg.user_id === user?.id;
                  const showName = !isOwn && item.isFirstInGroup && currentChat?.is_group;
                  const showAvatar = !isOwn && item.isLastInGroup;
                  return (
                    <div
                      key={item.key}
                      className={`message-item ${isOwn ? 'own' : 'other'} ${item.isFirstInGroup ? 'group-first' : 'group-mid'} ${item.isLastInGroup ? 'group-last' : ''}`}
                    >
                      {!isOwn && (
                        <div className={`message-avatar ${showAvatar ? '' : 'message-avatar--spacer'}`}>
                          {showAvatar && renderAvatar(msg.user_avatar, getInitials(msg.user_name))}
                        </div>
                      )}
                      <div className="message-bubble">
                        {showName && (
                          <div className="message-sender-label" style={{ color: senderColor(msg.user_id) }}>
                            {msg.user_name}
                          </div>
                        )}
                        {msg.reply_to && (
                          <div className="message-reply-quote">
                            <span className="reply-quote-user">{msg.reply_user || 'Сообщение'}</span>
                            <span className="reply-quote-text">
                              {(msg.reply_text || '').substring(0, 60)}
                            </span>
                          </div>
                        )}
                        <div className="message-text">
                          {msg.is_deleted ? (
                            <em className="deleted-message">Сообщение удалено</em>
                          ) : (
                            msg.message
                          )}
                        </div>
                        {msg.has_attachment && (
                          <div
                            className="message-attachment"
                            onClick={() => handleOpenAttachment(msg.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleOpenAttachment(msg.id);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 4 }}>
                              <path d="M7.9175 17.8068L15.8084 10.2535C16.7558 9.34668 16.7558 7.87637 15.8084 6.96951C14.861 6.06265 13.325 6.06265 12.3776 6.96951L4.54387 14.4681C2.74382 16.1911 2.74382 18.9847 4.54387 20.7077C6.34391 22.4308 9.26237 22.4308 11.0624 20.7077L19.0105 13.0997C21.6632 10.5605 21.6632 6.44362 19.0105 3.90441C16.3578 1.3652 12.0569 1.3652 9.40419 3.90441L3 10.0346" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            {msg.attachment_name || 'Вложение'}
                          </div>
                        )}
                        <div className="message-footer">
                          <span className="message-time">
                            {formatTimeShort(msg.created_at)}
                            {msg.edited_at && ' · ред.'}
                          </span>
                          {isOwn && !msg.is_deleted && (
                            <span className={`read-ticks ${msg.is_read ? 'read' : ''}`}>
                              {msg.is_read ? '✓✓' : '✓'}
                            </span>
                          )}
                          {isOwn && !msg.is_deleted && (
                            <div className="message-actions">
                              <button onClick={() => setEditingMessage(msg)} className="edit-msg-btn" aria-label="Изменить">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                  <path d="M4 22H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                  <path d="M13.8881 3.66293L14.6296 2.92142C15.8581 1.69286 17.85 1.69286 19.0786 2.92142C20.3071 4.14999 20.3071 6.14188 19.0786 7.37044L18.3371 8.11195M13.8881 3.66293C13.8881 3.66293 13.9807 5.23862 15.3711 6.62894C16.7614 8.01926 18.3371 8.11195 18.3371 8.11195M13.8881 3.66293L7.07106 10.4799C6.60933 10.9416 6.37846 11.1725 6.17992 11.4271C5.94571 11.7273 5.74491 12.0522 5.58107 12.396C5.44219 12.6874 5.33894 12.9972 5.13245 13.6167L4.25745 16.2417M4.25745 16.2417L4.04356 16.8833C3.94194 17.1882 4.02128 17.5243 4.2485 17.7515C4.47573 17.9787 4.81182 18.0581 5.11667 17.9564L5.75834 17.7426L8.38334 16.8675C9.00282 16.6611 9.31256 16.5578 9.60398 16.4189C9.94775 16.2551 10.2727 16.0543 10.5729 15.8201C10.8275 15.6215 11.0584 15.3907 11.5201 14.9289L18.3371 8.11195M5.75834 17.7426L4.25745 16.2417" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                              <button onClick={() => handleDeleteMessage(msg.id)} className="delete-msg-btn" aria-label="Удалить">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                  <path d="M20.5001 6H3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                  <path d="M18.8332 8.5L18.3732 15.3991C18.1962 18.054 18.1077 19.3815 17.2427 20.1907C16.3777 21 15.0473 21 12.3865 21H11.6132C8.95235 21 7.62195 21 6.75694 20.1907C5.89194 19.3815 5.80344 18.054 5.62644 15.3991L5.1665 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                  <path d="M9.5 11L10 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                  <path d="M14.5 11L14 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                  <path d="M6.5 6C6.55588 6 6.58382 6 6.60915 5.99936C7.43259 5.97849 8.15902 5.45491 8.43922 4.68032C8.44784 4.65649 8.45667 4.62999 8.47434 4.57697L8.57143 4.28571C8.65431 4.03708 8.69575 3.91276 8.75071 3.8072C8.97001 3.38607 9.37574 3.09364 9.84461 3.01877C9.96213 3 10.0932 3 10.3553 3H13.6447C13.9068 3 14.0379 3 14.1554 3.01877C14.6243 3.09364 15.03 3.38607 15.2493 3.8072C15.3043 3.91276 15.3457 4.03708 15.4286 4.28571L15.5257 4.57697C15.5433 4.62992 15.5522 4.65651 15.5608 4.68032C15.841 5.45491 16.5674 5.97849 17.3909 5.99936C17.4162 6 17.4441 6 17.5 6" stroke="currentColor" strokeWidth="1.5" />
                                </svg>
                              </button>
                              <button onClick={() => setReplyingTo(msg)} className="reply-msg-btn" aria-label="Ответить">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                  <path d="M9.5 17L4.5 12L9.5 7M4.5 12L14.5 12C16.1667 12 19.5 13 19.5 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
              
              {attachments.length > 0 && (
                <div className="attachments-preview">
                  {attachments.map((att, idx) => (
                    <div key={idx} className="attachment-item">
                      <span>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 4 }}>
                          <path d="M7.9175 17.8068L15.8084 10.2535C16.7558 9.34668 16.7558 7.87637 15.8084 6.96951C14.861 6.06265 13.325 6.06265 12.3776 6.96951L4.54387 14.4681C2.74382 16.1911 2.74382 18.9847 4.54387 20.7077C6.34391 22.4308 9.26237 22.4308 11.0624 20.7077L19.0105 13.0997C21.6632 10.5605 21.6632 6.44362 19.0105 3.90441C16.3578 1.3652 12.0569 1.3652 9.40419 3.90441L3 10.0346" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                        {att.name} ({formatFileSize(att.size)})
                      </span>
                      <button onClick={() => removeAttachment(idx)}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M19.0068 5L5.00684 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          <path d="M19 19L5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {editingMessage && (
                <div className="edit-message-area">
                  <input
                    type="text"
                    defaultValue={editingMessage.message}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleEditMessage(editingMessage.id, e.target.value);
                      }
                    }}
                    placeholder="Редактировать сообщение..."
                    autoFocus
                  />
                  <button onClick={() => setEditingMessage(null)}>Отмена</button>
                </div>
              )}
              
              <div className="composer-area">
                <div className="composer">
                  <button
                    className="composer-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title="Прикрепить файл"
                    type="button"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M7.9175 17.8068L15.8084 10.2535C16.7558 9.34668 16.7558 7.87637 15.8084 6.96951C14.861 6.06265 13.325 6.06265 12.3776 6.96951L4.54387 14.4681C2.74382 16.1911 2.74382 18.9847 4.54387 20.7077C6.34391 22.4308 9.26237 22.4308 11.0624 20.7077L19.0105 13.0997C21.6632 10.5605 21.6632 6.44362 19.0105 3.90441C16.3578 1.3652 12.0569 1.3652 9.40419 3.90441L3 10.0346" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={handleFileUpload}
                  />
                  <textarea
                    ref={composerInputRef}
                    className="composer-input"
                    placeholder="Введите сообщение..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    rows={1}
                  />
                  <button
                    className="composer-send-btn"
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() && attachments.length === 0}
                    title="Отправить"
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.4975 18.4851L20.6281 9.09373C21.8764 5.34874 22.5006 3.47624 21.5122 2.48782C20.5237 1.49939 18.6511 2.12356 14.906 3.37189L5.57477 6.48218C3.49295 7.1761 2.45203 7.52305 2.13608 8.28637C2.06182 8.46577 2.01692 8.65596 2.00311 8.84963C1.94433 9.67365 2.72018 10.4495 4.27188 12.0011L4.55451 12.2837C4.80921 12.5384 4.93655 12.6658 5.03282 12.8075C5.22269 13.0871 5.33046 13.4143 5.34393 13.7519C5.35076 13.9232 5.32403 14.1013 5.27057 14.4574C5.07488 15.7612 4.97703 16.4131 5.0923 16.9147C5.32205 17.9146 6.09599 18.6995 7.09257 18.9433C7.59255 19.0656 8.24576 18.977 9.5522 18.7997L9.62363 18.79C9.99191 18.74 10.1761 18.715 10.3529 18.7257C10.6738 18.745 10.9838 18.8496 11.251 19.0285C11.3981 19.1271 11.5295 19.2585 11.7923 19.5213L12.0436 19.7725C13.5539 21.2828 14.309 22.0379 15.1101 21.9985C15.3309 21.9877 15.5479 21.9365 15.7503 21.8474C16.4844 21.5244 16.8221 20.5113 17.4975 18.4851Z" stroke="currentColor" strokeWidth="2.5" />
                      <path d="M6 18L21 3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-chat-selected">
              <div className="no-chat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 13.5997 2.37562 15.1116 3.04346 16.4525C3.22094 16.8088 3.28001 17.2161 3.17712 17.6006L2.58151 19.8267C2.32295 20.793 3.20701 21.677 4.17335 21.4185L6.39939 20.8229C6.78393 20.72 7.19121 20.7791 7.54753 20.9565C8.88837 21.6244 10.4003 22 12 22Z" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M8 12H8.009M11.991 12H12M15.991 12H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="no-chat-text">Выберите чат или начните новый диалог</div>
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно нового личного чата */}
      {showNewChatModal && (
        <div className="modal" onClick={() => setShowNewChatModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Новый личный чат</h2>
              <button className="close-btn" onClick={() => setShowNewChatModal(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M19.0068 5L5.00684 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M19 19L5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            {modalError && <div className="ui-inline-error" style={{ marginBottom: 12 }}>{modalError}</div>}
            <div className="users-list">
              {users.map(u => (
                <div
                  key={u.id}
                  className="user-item"
                  onClick={() => handleNewChat(u.id)}
                >
                  <div className="user-avatar">
                    {renderAvatar(u.avatar, getInitials(u.name))}
                    {u.online && <span className="online-dot" title="В сети" />}
                  </div>
                  <div className="user-info">
                    <div className="user-name">{u.name}</div>
                    <div className="user-role">
                      {u.role_name || u.role}
                      {u.online && <span className="status-online-inline"> · в сети</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно создания группы */}
      {showGroupModal && (
        <div className="modal" onClick={() => setShowGroupModal(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Создать групповой чат</h2>
              <button className="close-btn" onClick={() => setShowGroupModal(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M19.0068 5L5.00684 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M19 19L5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="form-group">
              <label>Название группы</label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Введите название"
              />
            </div>
            <div className="form-group">
              <label>Описание (необязательно)</label>
              <textarea
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Описание группы"
                rows={2}
              />
            </div>
            <div className="form-group">
              <label>Выберите участников</label>
              <div className="users-select-list">
                {users.map(u => u.id !== user?.id && (
                  <div
                    key={u.id}
                    className={`user-select-item ${selectedUsers.includes(u.id) ? 'selected' : ''}`}
                    onClick={() => toggleUserSelection(u.id)}
                  >
                    <div className="user-avatar-small">
                      {renderAvatar(u.avatar, getInitials(u.name))}
                      {u.online && <span className="online-dot small" title="В сети" />}
                    </div>
                    <span>{u.name}</span>
                  </div>
                ))}
              </div>
            </div>
            {modalError && <div className="ui-inline-error" style={{ marginBottom: 12 }}>{modalError}</div>}
            <button className="btn-primary" onClick={handleCreateGroup}>
              Создать группу
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default MessagesPage;
